"use client";

import { useEffect, useRef } from "react";
import { useTransition } from "@/context/TransitionContext";
import gsap from "gsap";
import { ScrollSmoother } from "gsap/all";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";

const PageTransition = () => {
    const { isTransitioning, setIsTransitioning, nextRoute, setNextRoute } = useTransition();
    const router = useRouter();
    const pathname = usePathname();
    const containerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isTransitioning || !nextRoute) return;

        const tl = gsap.timeline({
            onComplete: () => {
                setIsTransitioning(false);
                setNextRoute(null);
            },
        });

        // Initial state
        gsap.set(overlayRef.current, { yPercent: -100 });
        gsap.set(containerRef.current, { zIndex: 9999, pointerEvents: "all" });

        // Animation sequence
        tl.to(overlayRef.current, {
            yPercent: 0,
            duration: 0.8,
            ease: "power3.inOut",
        })
            .call(() => {
                const smoother = ScrollSmoother.get();
                if (smoother) {
                    smoother.scrollTo(0, false);
                } else {
                    window.scrollTo(0, 0);
                }

                const target = new URL(nextRoute, window.location.origin);
                const targetPath = target.pathname;

                // Use a full page navigation for cross-route transitions so every page
                // mounts from a clean state (avoids stale GSAP/ScrollSmoother state).
                if (targetPath !== pathname) {
                    window.location.assign(target.toString());
                    return;
                }

                router.push(nextRoute);
            })
            .to({}, { duration: 0.3 })
            .to(overlayRef.current, {
                yPercent: -100,
                duration: 0.8,
                ease: "power3.inOut",
            })
            .set(containerRef.current, { zIndex: -1, pointerEvents: "none" });

    }, [isTransitioning, nextRoute, pathname, router, setIsTransitioning, setNextRoute]);

    if (!isTransitioning || !nextRoute) return null;

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 w-full h-full pointer-events-none z-[9999] overflow-hidden"
        >
            {/* Brown Overlay with Centered Logo */}
            <div
                ref={overlayRef}
                className="absolute inset-0 w-full h-full bg-[#523122] flex items-center justify-center z-40 -translate-y-full"
            >
                {/* Red Circle Background */}
                <div className="relative w-40 h-40 md:w-56 md:h-56 bg-[#D2170D] rounded-full flex items-center justify-center">

                    {/* Logo */}
                    <Image
                        src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472158/logo-transparent_tsxorm.png"
                        alt="Kunafa Mafias Logo"
                        width={176}
                        height={176}
                        className="w-32 h-32 md:w-44 md:h-44 object-contain rounded-full"
                        priority
                    />
                </div>
            </div>
        </div>
    );
};

export default PageTransition;
